import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormLabel } from '@mui/material';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from "../../../Config/firebase.ts";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { ActionType, SubscriptionType } from '../../../enums.ts';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { logAction } from '../utilityFunctions.ts';

interface Item {
    name: string;
    price: string;
    quantity: string;
}

// Professional currency formatting function
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
};

export default function OrderMarkPaidConfirm() {

    const Dispatch = useAppDispatch();
    const ServiceProviderID = useAppSelector(selectEffectiveUID)
    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}

    const [prevPrice, setPrevPrice] = React.useState<number>(0);

    const open = useAppSelector((state: RootState) => state.ModalSlice.OrderMarkPaidConfirmModalOpen);

    function handleClose() {
        resetStates();
        Dispatch(closeModal("OrderMarkPaidConfirmModalOpen"))
    }

    const OrderId: string = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMarkOrderPaidModal.orderId)
    const subscriptionId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForMarkOrderPaidModal.subscriptionId);

    // Add new state for subscription plans
    const [cyclePriceArray, setCyclePriceArray] = React.useState<any[]>([]);
    const [selectedCyclePlan, setSelectedCyclePlan] = React.useState<any>(null);
    const [amountPaid, setAmountPaid] = React.useState<string>("");
    const [studentName, setStudentName] = React.useState("");
    const [subscriptionName, setSubscriptionName] = React.useState("");
    const [subscriptionType, setSubscriptionType] = React.useState<string>("");
    const [livesubscriptionId, setLivesubscriptionId] = React.useState<string>("");

    // Items state
    const [items, setItems] = React.useState<Item[]>([]);
    const [itemErrors, setItemErrors] = React.useState<{ name: boolean, price: boolean, quantity: boolean }[]>([]);
    const [bQuickCollect, setBQuickCollect] = React.useState<boolean>(false);
    const [orderNote, setOrderNote] = React.useState<string>("");

    // Add useEffect to fetch subscription and order details
    React.useEffect(() => {
        const fetchData = async () => {

            if (OrderId) {
                try {

                    const orderDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Orders/${OrderId}`);
                    const orderDoc = await getDoc(orderDocRef);

                    if (orderDoc.exists()) {
                        const orderData = orderDoc.data();
                        const isQuickCollect = Boolean(orderData.bQuickCollect);

                        setSubscriptionName(orderData.subscriptionName || "");
                        setStudentName(orderData.studentName || "");
                        setSubscriptionType(orderData.subscriptionType);
                        setCyclePriceArray(orderData.assignedPlans)
                        setSelectedCyclePlan(orderData.assignedPlans?.[orderData.assignedPlanIndex || 0]);
                        setPrevPrice(orderData.chargeable || 0);
                        setLivesubscriptionId(orderData.livesubscriptionId || "");
                        setBQuickCollect(isQuickCollect);

                        // Handle items first to calculate their total
                        let itemsArray: Item[] = [];
                        if (orderData.items && orderData.items.length > 0) {
                            itemsArray = orderData.items.map((item: any) => ({
                                name: item.name ?? '',
                                price: item.price !== undefined && item.price !== null ? String(item.price) : '',
                                quantity: item.quantity !== undefined && item.quantity !== null ? String(item.quantity) : '1',
                            }));
                            setItems(itemsArray);
                            setItemErrors(itemsArray.map(() => ({ name: false, price: false, quantity: false })));
                        } else if (isQuickCollect) {
                            // Quick Collect orders must have at least one item
                            itemsArray = [{ name: '', price: '', quantity: '1' }];
                            setItems(itemsArray);
                            setItemErrors([{ name: false, price: false, quantity: false }]);
                        } else {
                            setItems([]);
                            setItemErrors([]);
                        }

                        // Calculate items total from current items
                        const itemsTotal = itemsArray.reduce((total, item) => {
                            const priceNum = parseFloat(item.price) || 0;
                            const quantityNum = parseFloat(item.quantity) || 0;
                            return total + (priceNum * quantityNum);
                        }, 0);

                        // amountPaid = chargeable - (sum of item prices)
                        const baseOrderAmount = orderData.chargeable - itemsTotal;

                        setAmountPaid(baseOrderAmount.toString() || "");
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchData();
    }, [subscriptionId, OrderId, ServiceProviderID, open]);

    // Add amount handler
    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // Only allow numbers and decimals
        if (!value) {
            setAmountPaid("");
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') {
                // valid number
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setAmountPaid(value);
    };

    // Calculate total for an item
    const calculateItemTotal = (price: string, quantity: string) => {
        const priceNum = parseFloat(price) || 0;
        const quantityNum = parseFloat(quantity) || 0;
        return priceNum * quantityNum;
    };

    // Calculate grand total from items
    const calculateGrandTotal = () => {
        return items.reduce((total, item) => {
            return total + calculateItemTotal(item.price, item.quantity);
        }, 0);
    };

    // Calculate overall total for submission: amountPaid + (sum of item prices)
    const calculateOverallTotal = () => {
        const baseAmount = parseFloat(amountPaid) || 0;
        const itemsTotal = calculateGrandTotal();
        return baseAmount + itemsTotal;
    };

    // Check if current item is complete
    const isItemComplete = (item: Item) => {
        return item.name.trim() !== '' && item.price.trim() !== '' && item.quantity.trim() !== '';
    };

    // Check if can add new item
    const canAddItem = () => {
        if (items.length === 0) return true;
        const lastItem = items[items.length - 1];
        return isItemComplete(lastItem);
    };

    // Update item field
    const updateItem = (index: number, field: keyof Item, value: string) => {
        setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Add new item
    const addItem = () => {
        if (canAddItem()) {
            setItems(prev => [...prev, { name: '', price: '', quantity: '1' }]);
            setItemErrors(prev => [...prev, { name: false, price: false, quantity: false }]);
        }
    };

    // Remove item
    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(prev => prev.filter((_, i) => i !== index));
            setItemErrors(prev => prev.filter((_, i) => i !== index));
        } else if (items.length === 1 && !bQuickCollect) {
            // Only allow removing the last item if it's not a Quick Collect order
            setItems([]);
            setItemErrors([]);
        }
        // For Quick Collect orders, we don't allow removing the last item
    };

    // Handle price input (only numbers and decimal)
    const handlePriceChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value)) {
            updateItem(index, 'price', value);
        }
    };

    // Handle quantity input (positive numbers including decimals, not zero)
    const handleQuantityChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value) && value !== '0' && value !== '0.') {
            updateItem(index, 'quantity', value);
        }
    };

    // Check errors for items
    const checkItemErrors = () => {
        let hasErrors = false;
        const newItemErrors: { name: boolean, price: boolean, quantity: boolean }[] = [];

        items.forEach((item, index) => {
            const errors = {
                name: item.name.trim() === '',
                price: parseFloat(item.price) < 6,
                quantity: parseFloat(item.quantity) < 1
            };

            if (errors.name || errors.price || errors.quantity) {
                hasErrors = true;
            }

            newItemErrors[index] = errors;
        });

        setItemErrors(newItemErrors);
        return hasErrors;
    };

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/Orders`)
    function onSubmit(e: any) {
        e.preventDefault()

        // Check item errors for Count and MultiMonth
        if ((subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth) && items.length > 0) {
            const itemsHaveErrors = checkItemErrors();
            if (itemsHaveErrors) {
                return;
            }
        }

        const updateData = {
            orderType: "cash",
            paymentMethod: "cash",
            settlement_status: "Settled",
            payment_status: "Paid",
            paymentTime: Timestamp.fromDate(new Date()),
            settlementTime: Timestamp.fromDate(new Date()),
            updatedAt: Timestamp.fromDate(new Date()),
            chargeable: Number(calculateOverallTotal()),
            ...(subscriptionType === SubscriptionType.Count && selectedCyclePlan && {
                count: selectedCyclePlan.count,
                validity: selectedCyclePlan.validity,
                billingCycle: SubscriptionType.Count
            }),
            ...(subscriptionType === SubscriptionType.MultiMonth && selectedCyclePlan && {
                billingCycle: selectedCyclePlan.billingCycle,
                pauseDays: selectedCyclePlan.pauseDays
            }),

            // Add items for Count and MultiMonth
            ...((subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth) && items.length > 0 && {
                items: items.map(item => ({
                    name: item.name,
                    price: Number(item.price),
                    quantity: Number(item.quantity)
                }))
            }),
            // Add notes field
            orderNote: orderNote ? orderNote.trim() : ""
        };
        update(OrderId, updateData);

        logAction({
            action: ActionType.markPaid,
            timestamp: Timestamp.now(),
            platform: "web",
            performedBy: {
                spId: spId || "",
                name: spName || ""
            },
            livesubscriptionId: livesubscriptionId,
            context: {
                orderId: OrderId,
                prevPrice: prevPrice,
                livesubscriptionId: livesubscriptionId,
                newPrice: Number(calculateOverallTotal()),
                baseAmount: Number(amountPaid),
                itemsTotal: calculateGrandTotal(),
                bQuickCollect: bQuickCollect,
            },

        }, ServiceProviderID)

        Dispatch(closeModal("OrderMarkPaidConfirmModalOpen"))
    }

    function resetStates() {
        setAmountPaid("");
        setSelectedCyclePlan(null);
        setCyclePriceArray([]);
        setStudentName("");
        setSubscriptionName("");
        setItems([]);
        setItemErrors([]);
        setBQuickCollect(false);
        setOrderNote("");
    }


    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="sm"
            fullWidth
            open={open}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "40px" }} className={"bg-white"}>
                <div className={"header pb-[10px]"}>
                    <p className={"text-cf_heading font-[600] "}>Marking Order Paid?</p>

                </div>

                <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton></div>

                <div className={"pt-0"}>

                    <p className='font-semibold'>Received Cash or UPI?</p>

                    <p className='text-sm mt-2'> Name : {studentName}</p>
                    <p className='text-sm'>Subscription : {subscriptionName}</p>


                    {!bQuickCollect && <>
                        {subscriptionType === SubscriptionType.Count && cyclePriceArray.length > 0 && (
                            <div className="mt-4 space-y-4 mb-4">
                                <FormLabel component="legend" sx={{ color: 'black' }}>Selected Plan</FormLabel>

                                <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                    <div className="text-gray-500 text-sm">Classes</div>
                                    <div className="text-gray-500 text-sm">Price</div>
                                    <div className="text-gray-500 text-sm">Validity (Days)</div>
                                    <div></div>
                                </div>

                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2 pl-10">
                                    <div>{selectedCyclePlan?.count} classes</div>
                                    <div>₹{selectedCyclePlan?.price}</div>
                                    <div>{selectedCyclePlan?.validity} days</div>
                                </div>

                            </div>
                        )}

                        {subscriptionType === SubscriptionType.MultiMonth && cyclePriceArray.length > 0 && (
                            <div className="mt-4 space-y-4 mb-4">
                                <FormLabel component="legend" sx={{ color: 'black' }}>Selected Plan</FormLabel>

                                <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                    <div className="text-gray-500 text-sm">Plan</div>
                                    <div className="text-gray-500 text-sm">Price</div>
                                    <div className="text-gray-500 text-sm">Validity (Days)</div>
                                    <div></div>
                                </div>

                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm pl-10">
                                    <div>{selectedCyclePlan?.billingCycle}</div>
                                    <div>₹{selectedCyclePlan?.price}</div>
                                    <div>{selectedCyclePlan?.validity} days</div>
                                </div>

                            </div>
                        )}

                        {/* Amount Paid field - Show for Count/MultiMonth when not Quick Collect, never show for Monthly */}
                        {((subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth)) && <TextField
                            value={amountPaid}
                            onChange={handleAmountChange}
                            size="small"
                            label="Amount Paid"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey',
                                        borderRadius: '10px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'grey',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue',
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                    },
                                },
                                fontWeight: 400,
                                marginTop: 2
                            }}
                        />}
                    </>}

                    {/* Items Section - Only for Count and MultiMonth */}
                    {(subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth) && (
                        <div className="space-y-4 mt-6">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {bQuickCollect ? "Items" : "Additional Items"}
                                    </h3>
                                    {bQuickCollect && (
                                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-md font-medium">
                                            Quick Collect
                                        </span>
                                    )}
                                    {items.length === 0 && (
                                        <button
                                            onClick={addItem}
                                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                        >
                                            + Add Items
                                        </button>
                                    )}
                                </div>
                                {items.length > 0 && (
                                    <div className="text-right">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items Total</p>
                                        <p className="text-lg font-semibold text-gray-900 tabular-nums">{formatCurrency(calculateGrandTotal())}</p>
                                    </div>
                                )}
                            </div>

                            {items.length > 0 && (
                                <>
                                    {items.map((item, index) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-medium text-gray-700 text-sm">Item {index + 1}</h4>
                                                <div className="flex items-center gap-4">
                                                    {calculateItemTotal(item.price, item.quantity) > 0 && (
                                                        <div className="text-right">
                                                            <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200">
                                                                <span className="text-sm font-semibold tabular-nums">
                                                                    {formatCurrency(calculateItemTotal(item.price, item.quantity))}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {!(bQuickCollect && items.length === 1) && (
                                                        <button
                                                            onClick={() => removeItem(index)}
                                                            className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <TextField
                                                    value={item.name}
                                                    size='small'
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    label="Item Name"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="Enter item name"
                                                    error={itemErrors[index]?.name || false}
                                                    helperText={itemErrors[index]?.name ? "Item name is required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.price}
                                                    size='small'
                                                    onChange={(e) => handlePriceChange(index, e.target.value)}
                                                    label="Price"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="0.00"
                                                    error={itemErrors[index]?.price || false}
                                                    helperText={itemErrors[index]?.price ? "Minimum ₹6 required" : ""}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />

                                                <TextField
                                                    value={item.quantity}
                                                    size='small'
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    label="Quantity"
                                                    variant="outlined"
                                                    fullWidth
                                                    placeholder="1.0"
                                                    error={itemErrors[index]?.quantity || false}
                                                    helperText={itemErrors[index]?.quantity ? "Minimum 1 required" : ""}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                                borderRadius: '8px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        onClick={addItem}
                                        disabled={!canAddItem()}
                                        className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-all ${canAddItem()
                                            ? 'border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                            : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        + Add Item
                                    </button>
                                </>
                            )}

                            {/* Overall Total Section - Show for Count and MultiMonth with items */}
                            {calculateOverallTotal() > 0 && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100 border rounded-lg p-4 mt-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-sm text-gray-800 font-semibold tracking-wide">
                                                Total Chargeable Amount
                                            </p>
                                            <div className="text-xs text-gray-800 mt-1">
                                                {!bQuickCollect && parseFloat(amountPaid) > 0 && (
                                                    <span>Base: {formatCurrency(parseFloat(amountPaid))}</span>
                                                )}
                                                {items.length > 0 && calculateGrandTotal() > 0 && (
                                                    <span>{(!bQuickCollect && parseFloat(amountPaid) > 0) ? ' + ' : ''}Items: {formatCurrency(calculateGrandTotal())}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-gray-900 tabular-nums">
                                                {formatCurrency(calculateOverallTotal())}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Notes Section */}
                    <div className="mt-6">
                        <TextField
                            value={orderNote}
                            onChange={(e) => setOrderNote(e.target.value)}
                            size="small"
                            label="Notes (Optional)"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Add any additional notes about this payment..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey',
                                        borderRadius: '10px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'grey',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue',
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                    },
                                },
                                fontWeight: 400,
                            }}
                        />
                    </div>
                </div>

                <div className={"flex gap-2 pt-8"}>
                    <button onClick={(e) => {
                        e.preventDefault();
                        handleClose()
                    }}
                        className={"w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg"}>Cancel
                    </button>
                    <button
                        onClick={onSubmit}
                        className={"w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center"}>Confirm
                    </button>

                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
}